import React from 'react';
import {
  CurrencyDollar,
  CheckCircle,
  WarningCircle,
  TrendUp,
} from '@phosphor-icons/react';
import { Card, CardContent } from '../../components/ui/card';

const fmtMoney = (v) =>
  `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const StatCard = ({ icon: Icon, label, value, sublabel, tone = 'indigo', testid }) => {
  const tones = {
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-100',
    red: 'bg-red-50 text-red-700 border-red-100',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    amber: 'bg-amber-50 text-amber-700 border-amber-100',
  };
  return (
    <Card className={`border ${tones[tone]}`} data-testid={testid}>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider opacity-70 mb-1">{label}</p>
            <p className="text-3xl font-bold" style={{ fontFamily: 'Manrope' }}>
              {value}
            </p>
            {sublabel && <p className="text-xs mt-2 opacity-80">{sublabel}</p>}
          </div>
          <Icon size={28} weight="duotone" className="opacity-60" />
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * 4-card stat grid for the Denials dashboard.
 * Reads from `summary.totals` returned by GET /api/denials/summary.
 */
const DenialsStatsCards = ({ summary, loading }) => {
  if (loading || !summary) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} className="border-slate-200">
            <CardContent className="pt-6">
              <div className="h-16 animate-pulse bg-slate-100 rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }
  const t = summary.totals;
  const topReason = summary.top_carc?.[0];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="denials-stats-grid">
      <StatCard
        icon={WarningCircle}
        label="Total Denied"
        value={t.total_denied}
        sublabel={`${t.open} open · ${t.resolved} resolved · ${t.voided} voided`}
        tone="red"
        testid="stat-total-denied"
      />
      <StatCard
        icon={CurrencyDollar}
        label="Denied Amount"
        value={fmtMoney(t.total_denied_amount)}
        sublabel={`Recovered: ${fmtMoney(t.total_recovered)}`}
        tone="amber"
        testid="stat-denied-amount"
      />
      <StatCard
        icon={CheckCircle}
        label="Recovery Rate"
        value={`${t.recovery_rate}%`}
        sublabel={`${t.resolved} of ${t.total_denied} resolved`}
        tone="emerald"
        testid="stat-recovery-rate"
      />
      <StatCard
        icon={TrendUp}
        label="Top Reason"
        value={topReason ? `CARC ${topReason.code}` : '—'}
        sublabel={
          topReason
            ? `${topReason.count}× · ${fmtMoney(topReason.amount)} · ${topReason.label.slice(0, 48)}${topReason.label.length > 48 ? '…' : ''}`
            : 'No denials in current filter'
        }
        tone="indigo"
        testid="stat-top-reason"
      />
    </div>
  );
};

export default DenialsStatsCards;
